// /app/frontend/src/pages/compliance/maturity/MaturityReviewQueuePage.jsx
/**
 * DPO Review Queue for SAQ Control Suggestions
 * Sprint 2 Frontend - Review and confirm SAQ-generated maturity suggestions
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { maturityAPI, entitiesAPI } from '../../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { toast } from 'sonner';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import {
  ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, ClipboardList,
  Building2, ArrowLeft, Filter, Search, BarChart3, Flag, Clock
} from 'lucide-react';

// Maturity level configuration
const MATURITY_LEVELS = {
  1: { name: 'Reactive', color: '#EF4444', bg: 'bg-red-500/10', text: 'text-red-600', badge: 'bg-red-100 text-red-800' },
  2: { name: 'Provisional', color: '#F59E0B', bg: 'bg-amber-500/10', text: 'text-amber-600', badge: 'bg-amber-100 text-amber-800' },
  3: { name: 'Formalized', color: '#10B981', bg: 'bg-emerald-500/10', text: 'text-emerald-600', badge: 'bg-emerald-100 text-emerald-800' },
  4: { name: 'Monitored', color: '#3B82F6', bg: 'bg-blue-500/10', text: 'text-blue-600', badge: 'bg-blue-100 text-blue-800' },
  5: { name: 'Proactive', color: '#8B5CF6', bg: 'bg-violet-500/10', text: 'text-violet-600', badge: 'bg-violet-100 text-violet-800' }
};

// Domain configuration
const DOMAIN_CONFIG = {
  GOV: { name: 'Governance & Accountability', color: '#3B82F6', order: 1 },
  RCT: { name: 'Rights, Consent & Transparency', color: '#8B5CF6', order: 2 },
  DLM: { name: 'Data Lifecycle Management', color: '#06B6D4', order: 3 },
  SEC: { name: 'Information Security', color: '#10B981', order: 4 },
  TPM: { name: 'Third-Party Management', color: '#F59E0B', order: 5 },
  INC: { name: 'Incident & Breach Management', color: '#EF4444', order: 6 }
};

// Response distribution bar
function ResponseDistribution({ distribution = {} }) {
  const total = Object.values(distribution).reduce((sum, v) => sum + v, 0) || 1;
  const options = [
    { key: 'we_do_this', label: '✓', color: 'bg-emerald-500', score: 5 },
    { key: 'we_mostly_do_this', label: 'M', color: 'bg-blue-500', score: 4 },
    { key: 'we_partially_do_this', label: 'P', color: 'bg-amber-500', score: 3 },
    { key: 'we_plan_to_do_this', label: '→', color: 'bg-slate-400', score: 2 },
    { key: 'we_dont_do_this', label: '✗', color: 'bg-red-500', score: 1 },
  ];

  return (
    <div className="flex gap-1 items-center">
      {options.map(opt => {
        const count = distribution[opt.key] || 0;
        const pct = total > 0 ? (count / total) * 100 : 0;
        return (
          <div key={opt.key} className="flex-1 flex flex-col items-center gap-0.5">
            <div className={`w-full h-6 ${opt.color} rounded transition-opacity ${pct === 0 ? 'opacity-20' : ''}`} title={`${opt.label}: ${count}`} />
            <span className="text-xs text-slate-500">{count}</span>
          </div>
        );
      })}
      <span className="text-xs text-slate-500 ml-2">n={total}</span>
    </div>
  );
}

// Confirmation modal component
function ConfirmationModal({ control, onConfirm, onCancel, isSubmitting }) {
  const [level, setLevel] = useState(control.suggested_level || 3);
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState('');

  const handleConfirm = () => {
    if (note.length < 10) {
      setNoteError('Note must be at least 10 characters');
      return;
    }
    onConfirm(level, note);
  };

  const levelConfig = MATURITY_LEVELS[level] || MATURITY_LEVELS[3];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-2xl dark:bg-slate-900 dark:border-slate-700 my-auto">
        <CardHeader className="border-b dark:border-slate-700">
          <CardTitle className="flex items-center gap-2 dark:text-white">
            <ClipboardList className="w-5 h-5" />
            Confirm: {control.control_id} - {control.control_title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          {/* Question */}
          <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">SAQ Question:</p>
            <p className="text-sm font-medium dark:text-slate-200">{control.question_text}</p>
          </div>

          {/* Response Summary */}
          <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">Response Distribution:</p>
            <ResponseDistribution distribution={control.score_distribution} />
          </div>

          {/* Suggested vs Prior */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-600 dark:text-slate-400 mb-1">SAQ Suggested:</p>
              <p className={`font-bold text-lg ${MATURITY_LEVELS[control.suggested_level].text}`}>
                Level {control.suggested_level}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Score: {control.normalised_score?.toFixed(1)}%
              </p>
            </div>
            {control.prior_assessed_level && (
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                <p className="text-xs text-slate-600 dark:text-slate-400 mb-1">Prior Assessment:</p>
                <p className={`font-bold text-lg ${MATURITY_LEVELS[control.prior_assessed_level].text}`}>
                  Level {control.prior_assessed_level}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {new Date(control.prior_assessment_date).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>

          {/* Level Selector */}
          <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-900">
            <p className="text-sm font-medium mb-3 dark:text-slate-200">Confirm Maturity Level:</p>
            <div className="flex gap-2 flex-wrap">
              {[1, 2, 3, 4, 5].map(lvl => (
                <button
                  key={lvl}
                  onClick={() => setLevel(lvl)}
                  className={`px-4 py-2 rounded font-medium text-sm transition-all ${
                    level === lvl
                      ? `${MATURITY_LEVELS[lvl].badge} ring-2 ring-offset-2 dark:ring-offset-slate-900 ring-slate-400`
                      : `bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700`
                  }`}
                >
                  L{lvl}: {MATURITY_LEVELS[lvl].name}
                </button>
              ))}
            </div>
          </div>

          {/* Override indicator */}
          {level !== control.suggested_level && (
            <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-900 dark:text-amber-300">Override detected</p>
                <p className="text-amber-700 dark:text-amber-200">You're confirming Level {level} instead of the suggested Level {control.suggested_level}. Please explain your rationale in the note below.</p>
              </div>
            </div>
          )}

          {/* Note textarea */}
          <div>
            <label className="block text-sm font-medium mb-2 dark:text-slate-300">DPO Rationale (min. 10 characters):</label>
            <textarea
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                if (e.target.value.length >= 10) setNoteError('');
              }}
              placeholder="Explain your assessment decision..."
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="4"
            />
            {noteError && <p className="text-xs text-red-600 dark:text-red-400 mt-1">{noteError}</p>}
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{note.length}/10 minimum</p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 justify-end border-t dark:border-slate-700 pt-4">
            <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isSubmitting || note.length < 10}
            >
              {isSubmitting ? 'Confirming...' : 'Confirm Control'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Control card component
function ControlCard({ control, onConfirmClick, isConfirmed }) {
  const [expanded, setExpanded] = useState(false);
  const domainConfig = DOMAIN_CONFIG[control.control_domain] || {};
  const levelConfig = MATURITY_LEVELS[control.suggested_level] || MATURITY_LEVELS[3];

  return (
    <Card className={`border rounded-lg overflow-hidden ${isConfirmed ? 'border-emerald-200 dark:border-emerald-700 bg-emerald-50/30 dark:bg-emerald-950/30' : 'border-slate-200 dark:border-slate-700'}`}>
      <div
        className="p-4 cursor-pointer hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between gap-4">
          {/* Control ID & Title */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <h3 className="font-bold text-slate-900 dark:text-white text-sm">{control.control_id}</h3>
              {isConfirmed && (
                <Badge className="bg-emerald-100 text-emerald-800 text-xs">✓ Confirmed</Badge>
              )}
              {control.reconciliation_flagged && (
                <Badge className="bg-amber-100 text-amber-800 text-xs flex items-center gap-1">
                  <Flag className="w-3 h-3" />
                  Reconciliation
                </Badge>
              )}
              {control.divergence_detected && (
                <Badge className="bg-orange-100 text-orange-800 text-xs">Divergence</Badge>
              )}
            </div>
            <p className="text-sm text-slate-700 dark:text-slate-300 font-medium">{control.control_title}</p>
          </div>

          {/* Right side: Score Badge + Expand Icon */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className={`px-3 py-2 rounded font-bold text-sm text-center ${levelConfig.badge}`}>
              <div>L{control.suggested_level}</div>
              <div className="text-xs font-normal">{control.normalised_score?.toFixed(0)}%</div>
            </div>
            <button className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-400">
              {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-slate-200 dark:border-slate-700 px-4 py-4 space-y-4 bg-white dark:bg-slate-900">
          {/* Question */}
          <div>
            <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">SAQ Question ({control.saq_question_id})</p>
            <p className="text-sm text-slate-700 dark:text-slate-300">{control.question_text}</p>
          </div>

          {/* Response Distribution */}
          <div>
            <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-2">Response Distribution ({control.respondent_count} respondents)</p>
            <ResponseDistribution distribution={control.score_distribution} />
          </div>

          {/* Prior Assessment */}
          {control.prior_assessed_level && (
            <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded border border-blue-100 dark:border-blue-900">
              <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Prior Assessment</p>
              <p className={`text-sm font-medium ${MATURITY_LEVELS[control.prior_assessed_level].text}`}>
                Level {control.prior_assessed_level} - {MATURITY_LEVELS[control.prior_assessed_level].name}
                <span className="text-xs text-slate-500 dark:text-slate-400 ml-2">
                  ({new Date(control.prior_assessment_date).toLocaleDateString()})
                </span>
              </p>
            </div>
          )}

          {/* DPO Confirmation if exists */}
          {isConfirmed && control.dpo_confirmation && (
            <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 rounded border border-emerald-100 dark:border-emerald-900">
              <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-2">DPO Confirmation</p>
              <p className="text-sm font-medium mb-1">
                Level {control.dpo_confirmation.confirmed_level}
                {control.dpo_confirmation.dpo_override && (
                  <span className="text-xs text-amber-600 dark:text-amber-400 ml-2">(Override)</span>
                )}
              </p>
              <p className="text-sm text-slate-700 dark:text-slate-300 mb-2">{control.dpo_confirmation.dpo_note}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Confirmed by {control.dpo_confirmation.confirmed_by} on{' '}
                {new Date(control.dpo_confirmation.confirmation_date).toLocaleDateString()}
              </p>
            </div>
          )}

          {/* Action Button */}
          {!isConfirmed && (
            <Button
              onClick={() => onConfirmClick(control)}
            >
              Confirm Control
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

// Main page component
export default function MaturityReviewQueuePage() {
  const { entityId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [queue, setQueue] = useState(null);
  const [summary, setSummary] = useState(null);
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filterTab, setFilterTab] = useState('all'); // all, needs-review, reconciliation, confirmed
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedControl, setSelectedControl] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);

  // Load review queue and summary
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get entity details
        console.log('Fetching entity with ID:', entityId);
        const entityRes = await entitiesAPI.getOne(entityId);
        console.log('Entity API response:', entityRes);
        const entityData = entityRes?.data || entityRes;
        console.log('Entity data to set:', entityData);
        setEntity(entityData || { entity_name: 'Unknown Entity', jurisdiction: 'N/A' });

        // Get review queue summary
        const summaryRes = await maturityAPI.getReviewQueueSummary(entityId);
        const summaryData = summaryRes?.data || summaryRes;
        setSummary(summaryData);
        console.log('Summary loaded:', summaryData);

        // Get full review queue
        const queueRes = await maturityAPI.getReviewQueue(entityId);
        const queueData = queueRes?.data || queueRes;
        setQueue(queueData);
        console.log('Queue loaded:', queueData);
      } catch (err) {
        console.error('Failed to load review queue:', err);
        setError(err.message || 'Failed to load review queue');
        toast.error('Failed to load review queue');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [entityId]);

  // Filter controls
  const getFilteredControls = () => {
    if (!queue || !queue.controls) return [];

    let filtered = queue.controls;

    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        c =>
          c.control_id.toLowerCase().includes(term) ||
          c.control_title.toLowerCase().includes(term) ||
          c.question_text.toLowerCase().includes(term)
      );
    }

    // Apply filter
    switch (filterTab) {
      case 'needs-review':
        return filtered.filter(c => !c.is_confirmed);
      case 'reconciliation':
        return filtered.filter(c => c.reconciliation_flagged);
      case 'confirmed':
        return filtered.filter(c => c.is_confirmed);
      default:
        return filtered;
    }
  };

  // Handle confirmation
  const handleConfirm = async (control, level, note) => {
    try {
      setIsConfirming(true);

      await maturityAPI.confirmControl(control.control_id, {
        confirmed_level: level,
        dpo_note: note,
        campaign_id: queue.campaign_id,
        entity_id: entityId
      });

      toast.success(`Control ${control.control_id} confirmed at Level ${level}`);

      // Refresh data
      const queueRes = await maturityAPI.getReviewQueue(entityId);
      const updatedQueue = queueRes?.data || queueRes;
      setQueue(updatedQueue);

      const summaryRes = await maturityAPI.getReviewQueueSummary(entityId);
      const updatedSummary = summaryRes?.data || summaryRes;
      setSummary(updatedSummary);

      setSelectedControl(null);
    } catch (err) {
      console.error('Failed to confirm control:', err);
      toast.error(err.message || 'Failed to confirm control');
    } finally {
      setIsConfirming(false);
    }
  };

  const filteredControls = getFilteredControls();
  const confirmedCount = queue?.controls?.filter(c => c.is_confirmed).length || 0;
  const pendingCount = queue?.controls?.filter(c => !c.is_confirmed).length || 0;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-slate-600">Loading review queue...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="space-y-4">
          <Button onClick={() => navigate('/compliance/maturity')} variant="outline" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <Alert className="border-red-200 bg-red-50">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <AlertDescription className="text-red-800">{error}</AlertDescription>
          </Alert>
        </div>
      </DashboardLayout>
    );
  }

  if (!queue) {
    return (
      <DashboardLayout>
        <div className="space-y-4">
          <Button onClick={() => navigate('/compliance/maturity')} variant="outline" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <Alert>
            <AlertDescription>No review queue found. Please finalize a campaign first.</AlertDescription>
          </Alert>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <Button onClick={() => navigate('/compliance/maturity')} variant="outline" className="flex items-center gap-2 mb-4">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-1">DPO Review Queue</h1>
            <p className="text-slate-500 dark:text-slate-400 text-base">
              {entity?.entity_name ? `${entity.entity_name} (${entity.jurisdiction || 'N/A'})` : 'Loading entity details...'}
            </p>
          </div>
          <div className="text-right bg-slate-50 dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">Campaign ID</p>
            <p className="font-mono text-sm text-slate-900 dark:text-slate-100 break-all">{queue?.campaign_id || 'Loading...'}</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{summary?.total_controls || 0}</div>
              <div className="text-sm text-muted-foreground">Total Controls</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{summary?.dpo_reviewed || 0}</div>
              <div className="text-sm text-muted-foreground">Confirmed</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-amber-600 dark:text-amber-400">{summary?.draft || 0}</div>
              <div className="text-sm text-muted-foreground">Pending Review</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">{summary?.reconciliation_pending || 0}</div>
              <div className="text-sm text-muted-foreground">Reconciliation</div>
            </CardContent>
          </Card>
        </div>

        {/* Progress Card */}
        <Card>
          <CardContent className="p-6">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Review Progress</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {summary?.dpo_reviewed}/{summary?.total_controls} confirmed
                </p>
              </div>
              <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                <div
                  className="bg-emerald-500 dark:bg-emerald-400 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${
                      summary?.total_controls
                        ? (summary.dpo_reviewed / summary.total_controls) * 100
                        : 0
                    }%`
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Alert Banner */}
        {summary?.reconciliation_pending > 0 && (
          <Alert className="border-amber-200 bg-amber-50">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              {summary.reconciliation_pending} controls require reconciliation review due to perception-reality gaps.
            </AlertDescription>
          </Alert>
        )}

        {/* Filters & Search */}
        <Card>
          <CardContent className="p-4">
            <div className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search controls by ID, title, or question..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Filter Tabs */}
              <div className="flex gap-2 border-b border-slate-200">
                {[
                  { value: 'all', label: 'All Controls', count: queue?.controls?.length || 0 },
                  { value: 'needs-review', label: 'Needs Review', count: pendingCount },
                  { value: 'reconciliation', label: 'Reconciliation', count: summary?.reconciliation_pending || 0 },
                  { value: 'confirmed', label: 'Confirmed', count: confirmedCount }
                ].map(tab => (
                  <button
                    key={tab.value}
                    onClick={() => setFilterTab(tab.value)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                      filterTab === tab.value
                        ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                        : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                    }`}
                  >
                    {tab.label}
                    <span className="ml-2 inline-block text-xs bg-slate-100 dark:bg-slate-700 rounded px-2 py-0.5 font-semibold">{tab.count}</span>
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Controls List */}
        <div className="space-y-4">
          {filteredControls.length === 0 ? (
            <Card className="text-center py-16 border border-slate-200 dark:border-slate-700">
              <p className="text-lg text-slate-600 dark:text-slate-300 font-medium">No controls match your filters</p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">Try adjusting your search or filter criteria</p>
            </Card>
          ) : (
            filteredControls.map(control => (
              <ControlCard
                key={control.control_id}
                control={control}
                onConfirmClick={setSelectedControl}
                isConfirmed={control.is_confirmed}
              />
            ))
          )}
        </div>

        {/* Confirmation Modal */}
        {selectedControl && (
          <ConfirmationModal
            control={selectedControl}
            onConfirm={(level, note) => handleConfirm(selectedControl, level, note)}
            onCancel={() => setSelectedControl(null)}
            isSubmitting={isConfirming}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
